import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ServiceFormData, ServicePromotion } from '../types';
import { Calendar, Image as ImageIcon, Tag, Upload, X, ArrowRight, ArrowLeft, Check, Clock, DollarSign } from 'lucide-react';

interface ServiceFormProps {
  formData: ServiceFormData;
  onFormChange: (data: ServiceFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing: boolean;
  existingImageUrl?: string;
}

export function ServiceForm({ formData, onFormChange, onSubmit, onCancel, isEditing, existingImageUrl }: ServiceFormProps) {
  const [step, setStep] = useState(1);
  const [showPromotion, setShowPromotion] = useState(!!formData.promotion);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const event = new CustomEvent('ui:service-modal', { detail: { open: true } });
    window.dispatchEvent(event);
    return () => {
      const event = new CustomEvent('ui:service-modal', { detail: { open: false } });
      window.dispatchEvent(event);
    };
  }, []);

  useEffect(() => {
    if (existingImageUrl) {
      setPreviewUrl(existingImageUrl);
    }
  }, [existingImageUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFormChange({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    onFormChange({ ...formData, image: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePromotionChange = (field: keyof ServicePromotion, value: any) => {
    const updatedPromotion = {
      ...(formData.promotion || {
        is_active: true,
        discount_type: 'percentage',
        discount_value: 0
      }),
      [field]: value
    } as ServicePromotion;

    onFormChange({
      ...formData,
      promotion: updatedPromotion
    });
  };

  const canGoNext = () => {
    if (step === 1) return formData.name_he.trim().length > 0;
    if (step === 2) return formData.price && parseFloat(formData.price) > 0;
    if (step === 3) return formData.duration && parseInt(formData.duration) >= 5;
    return true;
  };

  const handleNext = () => {
    if (canGoNext()) {
      if (step < 4) setStep(step + 1);
      else onSubmit();
    }
  };

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center"
      style={{ zIndex: 9999 }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="bg-white w-full sm:max-w-lg sm:rounded-t-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ height: 'calc(100vh - 60px)', maxHeight: 'calc(100vh - 60px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-4 sm:rounded-t-2xl flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-gray-500 active:text-gray-700 rounded-lg active:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-bold text-gray-900">
              {isEditing ? 'עריכת שירות' : 'שירות חדש'}
            </h2>
            <div className="w-9" />
          </div>

          {/* Progress bar */}
          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="absolute inset-y-0 right-0 bg-gradient-to-l from-blue-500 to-blue-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">שלב {step} מתוך {totalSteps}</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">מה שם השירות?</h3>
                  <p className="text-sm text-gray-600">תן שם ברור ומתאר לשירות</p>
                </div>

                <input
                  type="text"
                  value={formData.name_he}
                  onChange={(e) => onFormChange({ ...formData, name_he: e.target.value })}
                  placeholder="לדוגמה: תספורת גבר"
                  className="w-full p-4 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  autoFocus
                />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <DollarSign className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">מה המחיר?</h3>
                  <p className="text-sm text-gray-600">הזן את מחיר השירות בשקלים</p>
                </div>

                <div className="relative">
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => onFormChange({ ...formData, price: e.target.value })}
                    placeholder="100"
                    className="w-full p-4 pr-12 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-right"
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-500">₪</span>
                </div>

                {formData.price && parseFloat(formData.price) > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 border border-green-200 rounded-xl p-3 text-center"
                  >
                    <p className="text-green-700 font-semibold">
                      מחיר: {parseFloat(formData.price).toFixed(2)} ₪
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">כמה זמן לוקח?</h3>
                  <p className="text-sm text-gray-600">משך הזמן בדקות</p>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[15, 30, 45, 60, 90, 120].map((minutes) => (
                    <button
                      key={minutes}
                      type="button"
                      onClick={() => onFormChange({ ...formData, duration: minutes.toString() })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        formData.duration === minutes.toString()
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-2xl font-bold">{minutes}</div>
                      <div className="text-xs text-gray-600">דקות</div>
                    </button>
                  ))}
                </div>

                <div className="relative">
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => onFormChange({ ...formData, duration: e.target.value })}
                    placeholder="30"
                    className="w-full p-4 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    min="5"
                    step="5"
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">דקות</span>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <ImageIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">פרטים נוספים</h3>
                  <p className="text-sm text-gray-600">תמונה, תיאור ומבצע (אופציונלי)</p>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">תמונה</label>
                  {previewUrl ? (
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="תצוגה מקדימה"
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
                    >
                      <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">לחץ להעלאת תמונה</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">תיאור</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
                    placeholder="תיאור קצר של השירות"
                    className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                    rows={3}
                  />
                </div>

                {/* Promotion Toggle */}
                <button
                  type="button"
                  onClick={() => {
                    setShowPromotion(!showPromotion);
                    if (!showPromotion) {
                      onFormChange({
                        ...formData,
                        promotion: {
                          is_active: true,
                          discount_type: 'percentage',
                          discount_value: 10
                        }
                      });
                    } else {
                      onFormChange({
                        ...formData,
                        promotion: undefined
                      });
                    }
                  }}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                    showPromotion
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Tag className="h-5 w-5" />
                    <span className="font-semibold">הוסף מבצע</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    showPromotion ? 'border-green-500 bg-green-500' : 'border-gray-300'
                  }`}>
                    {showPromotion && <Check className="h-3 w-3 text-white" />}
                  </div>
                </button>

                {showPromotion && formData.promotion && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-3 p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">סוג</label>
                        <select
                          value={formData.promotion.discount_type}
                          onChange={(e) => handlePromotionChange('discount_type', e.target.value)}
                          className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="percentage">אחוז</option>
                          <option value="fixed">סכום</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">ערך</label>
                        <input
                          type="number"
                          value={formData.promotion.discount_value}
                          onChange={(e) => handlePromotionChange('discount_value', parseFloat(e.target.value))}
                          className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="0"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 p-4 flex gap-3 sm:rounded-b-2xl flex-shrink-0">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
            >
              <ArrowRight className="h-5 w-5" />
              חזור
            </button>
          )}
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext()}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {step === totalSteps ? (
              <>
                <Check className="h-5 w-5" />
                שמור
              </>
            ) : (
              <>
                המשך
                <ArrowLeft className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
